import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Last line of defence for the Platform shell.
 *
 * Without a boundary, a throw anywhere during render unmounts React's whole tree and leaves
 * `#root` empty: the page goes fully white, and the only trace is a console message the
 * operator has no reason to open. That is exactly how "logged in, got a white page, clicking
 * anything goes white again" presents from the outside, and it says nothing about which
 * screen or which value caused it.
 *
 * This boundary keeps the failure on screen — message and stack — so the next click reports
 * the cause instead of hiding it. It is deliberately dependency-free (no i18n, no MUI theme,
 * no auth, inline styles only): it has to keep working when those are the things that broke.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown as the headline of the failure screen. */
  title?: string;
  /**
   * When this value changes the boundary clears its error. Route boundaries pass the current
   * pathname, so a screen that crashed does not poison every other screen: navigating away
   * remounts the subtree. Without it a single bad render would stick for the whole session.
   */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: unknown;
  seenResetKey: string | undefined;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, seenResetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.seenResetKey) {
      return { error: null, seenResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // Keep the console output: the boundary is a safety net, not a silencer.
    // eslint-disable-next-line no-console
    console.error('Platform shell crashed during render:', error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return <FailureScreen title={this.props.title ?? 'This screen failed to render'} error={this.state.error} />;
    }
    return this.props.children;
  }
}

/**
 * A boundary that resets on navigation, for use inside the router.
 *
 * Every routed screen sits under one of these so a throw is contained to the screen the
 * operator opened: the URL stays put, the message names the error, and any other link in the
 * shell still works.
 */
export function RouteBoundary({ children, title }: { children: React.ReactNode; title?: string }) {
  const location = useLocation();
  // `key` on the boundary itself, not just resetKey: React remounts on a key change, which also
  // clears any state the crashed subtree left in the boundary's children.
  return (
    <ErrorBoundary key={location.pathname} resetKey={location.pathname} title={title}>
      {children}
    </ErrorBoundary>
  );
}

/** The visible failure page. Inline styles, so it needs no stylesheet and no theme. */
export function FailureScreen({ title, error }: { title: string; error: unknown }) {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return JSON.stringify(error);
            } catch {
              return String(error);
            }
          })();
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#101828',
        background: '#f7f9fc',
      }}
    >
      <div
        style={{
          maxWidth: 'min(900px, 92vw)',
          border: '1px solid #e3e9ee',
          borderLeft: '4px solid #d92d20',
          borderRadius: 10,
          background: '#fff',
          padding: 20,
          display: 'grid',
          gap: 10,
        }}
      >
        <strong style={{ fontSize: 16 }}>{title}</strong>
        <span style={{ color: '#475467', fontSize: 14 }}>{message}</span>
        {stack ? (
          <pre
            style={{
              maxHeight: '46vh',
              overflow: 'auto',
              margin: 0,
              padding: 12,
              background: '#101828',
              color: '#e6edf3',
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.45,
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
            }}
          >
            {stack}
          </pre>
        ) : null}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => window.location.assign('/app')}
            style={{
              border: '1px solid #d0d5dd',
              background: '#fff',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
            }}
          >
            Go to the launchpad
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              border: '1px solid #d0d5dd',
              background: '#fff',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
